import { IntlShape } from 'react-intl';

import { IProductOptions, IServiceOption } from '../../_shared/interfaces/product-select';
import { ManufacturersStoryblok, ManufacturerStoryblok, ProductStoryblok, ServiceStoryblok } from '../../_shared/interfaces/storyblok';
import { IProduct, IProductGroup } from '../../components/order/services/services';
import { getDatasources, isGlobalDataSource } from '../helpers/helpers';
import { StoryblokCommon } from '../storyblok/storyblok';

export const getProductOptions = (
    manufacturers: ManufacturerStoryblok[] | undefined,
    storyblokCommon: StoryblokCommon,
    intl: IntlShape,
): { [manufacturer: string]: IProductGroup[] } => {
    const productOptions: { [manufacturer: string]: IProductGroup[] } = {};
    const datasourcesConfigurationType = isGlobalDataSource(storyblokCommon) ? 'categories_global' : 'category';
    const sortedCategoriesObjects = getDatasources(storyblokCommon, datasourcesConfigurationType, process.env.GATSBY_COUNTRY);
    const sortedCategories = sortedCategoriesObjects.map((e) => e.name);

    if (manufacturers) {
        manufacturers.forEach((m) => {
            if (!productOptions.hasOwnProperty(m.name)) {
                productOptions[m.name] = [];
            }
            m.products.forEach((p) => {
                // find or create category object
                let categoryObject = productOptions[m.name].find((c) => c.label === p.category);
                if (!categoryObject) {
                    categoryObject = { label: p.category, options: [] };
                    productOptions[m.name].push(categoryObject);
                }
                // get services for product
                const serviceOptions: IServiceOption[] = p.services.map((s) => {
                    let label: string;
                    const currency = storyblokCommon.configuration.content.currency;
                    if (parseFloat(s.price.toString()) > 0) {
                        label = `${s.name} (${intl.formatNumber(s.price, {
                            style: 'currency',
                            currency,
                        })})`;
                    } else {
                        label = `${s.name} ${storyblokCommon.configuration.content.order_service_no_price ?? ''}`;
                    }
                    return { label, value: s._uid, additionalCosts: s.additional_costs ?? false };
                });
                // push product in category object
                categoryObject.options.push({
                    label: p.name,
                    value: p.name,
                    serialNumber: p.serial_number,
                    softwareVersion: p.software_version,
                    services: serviceOptions,
                    image: p.image?.filename,
                    increasedLogisticsFee: p.increased_logistics_fee ?? false,
                });
            });
        });

        // Sort
        for (const key in productOptions) {
            if (productOptions.hasOwnProperty(key)) {
                productOptions[key].sort((a, b) => sortedCategories.indexOf(a.label) - sortedCategories.indexOf(b.label));
            }
        }

        return productOptions;
    } else {
        return {};
    }
};

export const getAllProductOptions = (manufacturers: ManufacturersStoryblok, storyblokCommon: StoryblokCommon): IProductOptions => {
    const allProducts: IProductOptions = {};
    const groupedProducts: any = {};
    const datasourcesConfigurationType = isGlobalDataSource(storyblokCommon) ? 'categories_global' : 'category';
    const sortedCategoriesObjects = getDatasources(storyblokCommon, datasourcesConfigurationType, process.env.GATSBY_COUNTRY);
    const sortedCategories = sortedCategoriesObjects.map((e) => e.name);

    manufacturers.manufacturer.forEach((m) => {
        m.products.forEach((p) => {
            if (!groupedProducts[m.name]) {
                groupedProducts[m.name] = {};
            }
            if (!groupedProducts[m.name][p.category]) {
                groupedProducts[m.name][p.category] = [];
            }
            if (!groupedProducts[m.name][p.category].includes(p.name)) {
                groupedProducts[m.name][p.category].push(p.name);
            }
        });

        for (const manufacturer in groupedProducts) {
            if (groupedProducts.hasOwnProperty(manufacturer)) {
                allProducts[manufacturer] = [];
                for (const category in groupedProducts[manufacturer]) {
                    if (groupedProducts[manufacturer].hasOwnProperty(category)) {
                        allProducts[manufacturer].push({
                            label: category,
                            options: groupedProducts[manufacturer][category].map((productName: string) => {
                                return { label: productName, value: productName };
                            }),
                        });
                    }
                }
                allProducts[manufacturer].sort((a, b) => sortedCategories.indexOf(a.label) - sortedCategories.indexOf(b.label));
            }
        }
    });

    return allProducts;
};

export const getOptionByValue = (options: IProductGroup[], value: string): IProduct | undefined => {
    for (const g of options) {
        for (const o of g.options) {
            if (o.value === value) {
                return o;
            }
        }
    }
};

export const getOptionByLabel = (options: IProductGroup[], value: string): IProduct | undefined => {
    for (const g of options) {
        for (const o of g.options) {
            if (o.label === value) {
                return o;
            }
        }
    }
};

export const getServiceById = (manufacturer: ManufacturerStoryblok | undefined, _uid: string): ServiceStoryblok | undefined => {
    if (!manufacturer) {
        return undefined;
    }
    for (const p of manufacturer?.products ?? []) {
        for (const s of p.services) {
            if (_uid === s._uid) {
                return s;
            }
        }
    }
    return undefined;
};

export const getProduct = (manufacturerName: string, productName: string, data: ManufacturersStoryblok): ProductStoryblok | undefined => {
    const manufacturer = data.manufacturer.find((m) => m.name === manufacturerName);
    if (manufacturer) {
        const product = (manufacturer.products as ProductStoryblok[]).find((p) => p.name === productName);
        if (product && product.service_interval) {
            return product;
        }
    }
    return undefined;
};
